import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ReactSVG } from 'react-svg'
import { useMediaQuery } from 'usehooks-ts'

import i18n from '@app/i18n'


import { useAuth } from '@features/auth'
import { useErrorInfoModal, useModalStore } from '@features/modal'
import { useAddressStore } from '@features/shipping-methods/model/useAddressStore'
import { AddressItemType, ShopItem } from '@features/shipping-methods/types'

import useSlots from '@widgets/slots/hooks/useSlots'

import {
  useMyAddressesQuery,
  useSuggestionsLazyQuery,
} from '@shared/generated'
import useShopTime from '@shared/hooks/useShopTime'
import { useYandexSuggest } from '@shared/hooks/useYandexSuggest'

import { Controls } from './Controls'
import DeleteAddressModal from './DeleteAddressModal'
import MapPickup from './MapPickup'
import { ShopDetails } from './ShopDetails'
import { ShopList } from './ShopList'

import styles from './styles.module.scss'


enum ViewState {
  INITIAL = 'initial',
  DETAILS = 'details',
  MAP = 'map',
}

type Props = {
  setHeaderConfig: React.Dispatch<
    React.SetStateAction<{
      title: string;
      icon?: boolean;
      onClick?: () => void;
      isTabs?: boolean;
      isMapRequired?: boolean;
    }>
  >;
};

export default function Pickup ({ setHeaderConfig }: Props) {
  const { t } = useTranslation('modalDelivery')
  const { t: te } = useTranslation('errors')
  const { openErrorInfoModal } = useErrorInfoModal()
  const isTablet = useMediaQuery('(max-width: 1279px)')
  const [ view, setView ] = useState<ViewState>(ViewState.INITIAL)
  const [ isSearchMode, setSearchMode ] = useState(false)
  const [ shops, setShops ] = useState<ShopItem[] | null>(null)
  const [ selectedShop, setSelectedShop ] = useState<ShopItem | null>(null)
  const [ mapCenter, setMapCenter ] = useState<[number, number] | null>(null)
  const { openModal } = useModalStore()
  const { saveAddress } = useAddressStore()
  const { openSlotsModal } = useSlots()
  const { closeModal } = useModalStore()
  const { requireRegistration } = useAuth()
  const {
    addAddress,
    setSelectedAddress,
    savedAddresses,
    setDeliveryType,
    setShopId,
  } = useAddressStore.getState()

  const [ fetchSuggestionDetails, data ] = useSuggestionsLazyQuery()
  const { data: { myAddresses: myShops } = {}, refetch } = useMyAddressesQuery({
    variables: { showPickupShops: true },
  })
  const { open, close, getShopStatus } = useShopTime(
    selectedShop?.open_time || 0,
    selectedShop?.close_time || 0,
  )
  const {
    query: searchInputAddress,
    setQuery: setSearchInputAddress,
    suggestions,
  } = useYandexSuggest({
    apiKey: import.meta.env.VITE_YANDEX_MAPS_API_KEY_SEARCH,
    lang: i18n.language === 'kk' ? 'kk_KZ' : 'ru_RU',
  })

  useEffect(() => {
    const suggestion =
      data?.data?.getAddressSuggestions?.suggestions?.[0]?.data

    if (suggestion?.geo_lat && suggestion?.geo_lon) {
      setMapCenter([ Number(suggestion.geo_lat), Number(suggestion.geo_lon) ])

      setSearchInputAddress('')
    }
  }, [ data, setSearchInputAddress ])

  const handleShopClick = (shop: ShopItem) => {
    setSelectedShop(shop)
    setView(ViewState.DETAILS)
  }

  const handleDeleteAddress = (shop: ShopItem) => {
    openModal(
      'deleteAddress',
      <DeleteAddressModal shop={shop} onSuccess={refetch} />,
      {
        title: t('shop.delete_title'),
      },
    )
  }

  const handleSelectShop = async (shop: ShopItem) => {
    const address = {
      shop_id: shop?.id,
      address: shop?.address || '',
      customerGeoPoint: {
        latitude: shop?.shopGeoPoint?.latitude,
        longitude: shop?.shopGeoPoint?.longitude,
      },
    }

    const isAlreadySaved =
      savedAddresses.some((addr) => addr.shop_id === shop?.id) ||
      myShops?.some((addr) => addr?.shop_id === shop?.id)

    if (isAlreadySaved) {
      setSelectedAddress(address as AddressItemType)
      setShopId(shop?.id as string)
      setDeliveryType('pickup')
      closeModal('delivery')
      openSlotsModal()
      return
    }

    try {
      if (requireRegistration) {
        addAddress(address as AddressItemType)
        setSelectedAddress(address as AddressItemType)
      }
      else {
        await saveAddress({
          addressInfo: address,
        })
      }

      setShopId(shop?.id as string)
      setDeliveryType('pickup')
      closeModal('delivery')
      openSlotsModal()
    }
    catch (err) {
      console.error('Error saving address:', err)
      openErrorInfoModal({
        description: te('select_shop'),
      })
    }
  }

  const handleControlsCallback = () => {
    setSearchMode(true)
    setView(ViewState.INITIAL)
  }

  useEffect(() => {
    if (isSearchMode || view === ViewState.DETAILS) {
      setHeaderConfig({
        title: t('touch-shop'),
        icon: true,
        onClick: () => {
          setView(ViewState.INITIAL)
          setSearchMode(false)
        },
        isTabs: false,
        isMapRequired: true,
      })
    }
    else {
      setHeaderConfig({ title: t('address'), isTabs: true, isMapRequired: true })
    }
    return () => {
      setHeaderConfig({ title: t('address'), isTabs: true, isMapRequired: true })
    }
  }, [ isSearchMode, setHeaderConfig, t, view ])

  return (
    <div className={styles.root__container}>
      <div className={styles.root__content}>
        <Controls
          view={view}
          handleControlsCallback={handleControlsCallback}
          isSearchMode={isSearchMode}
          setSearchMode={setSearchMode}
          isTablet={isTablet}
          value={searchInputAddress}
          handleChange={setSearchInputAddress}
          placeholder={t('input.placeholder-pickup')}
        />
        {isTablet && isSearchMode && (
          <span
            onClick={() => setView(ViewState.MAP)}
            className={styles.root__touchMap}
          >
            <ReactSVG src="/icons/map-pin.svg" wrapper="span" />
            {t('button.touch-map')}
          </span>
        )}
        {(!isTablet || view === ViewState.MAP) && (
          <MapPickup
            center={mapCenter}
            onShopsDataUpdate={(data) => {
              if (data !== undefined) {
                setShops(data)
              }
            }}
            onShopClick={handleShopClick}
          />
        )}

        {view === ViewState.DETAILS ? (
          <ShopDetails
            handleSelectShop={handleSelectShop}
            selectedShop={selectedShop}
            open={open}
            close={close}
            getShopStatus={getShopStatus}
          />
        ) : !suggestions.length && !(isTablet && view === ViewState.MAP) ? (
          <ShopList
            handleDeleteAddress={handleDeleteAddress}
            shops={
              (isSearchMode
                ? shops
                : requireRegistration
                  ? savedAddresses.filter((el) => el.shop_id)
                  : myShops) as ShopItem[]
            }
            isSearchMode={!isSearchMode}
            handleShopClick={handleShopClick}
          />
        ) : (
          suggestions.map((el, index) => (
            <div
              onClick={() => {
                fetchSuggestionDetails({
                  variables: { address: el.title.text },
                })
              }}
              className={styles.foundAddress}
              key={index}
            >
              <span className={styles.foundAddress__title}>
                {el.title.text}
              </span>
              <span className={styles.foundAddress__subtitle}>
                {el.subtitle?.text}
              </span>
            </div>
          ))
        )}
      </div>
    </div>
  )
}
